import { breadcrumbSchema, safeJsonLd } from "@/lib/structured-data";
import { createFileRoute, Link, notFound, redirect } from "@tanstack/react-router";
import { useSuspenseQuery, queryOptions } from "@tanstack/react-query";
import { sanitizeHtml } from "@/lib/sanitize-html";
import { normalizeBlogTypography } from "@/lib/blog-typography";
import { demoteHeadings } from "@/lib/blog-internal-links";
import { localizeExternalImages } from "@/lib/blog-external-images";
import { Header } from "@/components/site/Header";
import { Footer } from "@/components/site/Footer";
import { NotFoundPage } from "@/components/site/NotFoundPage";
import { getBlogPost } from "@/lib/blog.functions";
import { legacyResultsRedirect } from "@/lib/legacy-results-slugs";
import { legacyPageRedirect } from "@/lib/legacy-page-redirects";
import { withQuery } from "@/lib/redirect-query";
import { brandedTitle, htmlToText, snippet } from "@/lib/meta-text";
import { blogAuthorNode, isPersonAuthor, ORGANIZATION_ID } from "@/lib/blog-schema";
import { absoluteUrl } from "@/lib/absolute-url";
import { splitAfterFirstParagraph } from "@/lib/split-first-paragraph";
import { InlineQuoteCta } from "@/components/site/InlineQuoteCta";
import { PlayButton } from "@/components/site/PlayButton";
import { Fragment, useEffect, useRef, useState } from "react";
import {
  postCitations,
  placeVideos,
  postVideoObjects,
  youtubeEmbedUrl,
  POST_SOURCES,
  POST_VIDEOS,
  localCalendarDate,
  postDates,
  withTableOfContents,
  type PostDates,
  type PostSource,
  type PostVideo as PostVideoEntry,
  type TocItem,
} from "@/lib/blog-eeat";

const postQueryOptions = (slug: string) =>
  queryOptions({
    queryKey: ["blog-post", slug],
    queryFn: () => getBlogPost({ data: { slug } }),
  });

export const Route = createFileRoute("/$slug")({
  loader: async ({ context, params, location }) => {
    // Legacy Cyrillic-homoglyph URL for the LaserCap article (lаѕеrcap-trеаtmеnt
    // with Cyrillic а/ѕ/е lookalikes) — 301 to the corrected slug.
    if (params.slug === "lаѕеrcap-trеаtmеnt") {
      throw redirect({ href: withQuery("/lasercap-treatment/", location.searchStr), statusCode: 301 });
    }
    // Retired results-image posts — 301 to the real results content.
    const legacy = legacyResultsRedirect(params.slug);
    if (legacy) throw redirect({ href: withQuery(legacy, location.searchStr), statusCode: 301 });
    // Retired WordPress page slugs and clone-of-* duplicates still linked from
    // blog posts. Keep the query string so ad and UTM tags survive.
    const page = legacyPageRedirect(params.slug);
    if (page) throw redirect({ href: withQuery(page, location.searchStr), statusCode: 301 });
    const trace = (globalThis as { __rhMark?: (name: string) => void }).__rhMark;
    trace?.("loader.post");
    const post = await context.queryClient.ensureQueryData(postQueryOptions(params.slug));
    trace?.("loader.done");
    if (!post) throw notFound();
    // Legacy URLs (e.g. with Cyrillic lookalike letters) resolve to the post
    // but should land on the canonical slug.
    if (post.slug !== params.slug) {
      throw redirect({ href: withQuery(`/${post.slug}/`, location.searchStr), statusCode: 301 });
    }
    return post;
  },
  head: ({ loaderData }) => {
    (globalThis as { __rhMark?: (name: string) => void }).__rhMark?.("head.slug");
    if (!loaderData)
      return {
        meta: [
          { title: "Page Not Found | RESTORE Hair" },
          { name: "robots", content: "noindex, follow" },
        ],
      };
    const title = loaderData.meta_title || loaderData.title;
    // Use the first of meta_description, excerpt and post body that has at
    // least 50 characters, cut at a word boundary. Shorter values are
    // placeholders from the WordPress import (for example "Trending News").
    const candidates = [
      (loaderData.meta_description ?? "").trim(),
      snippet(loaderData.excerpt || ""),
      snippet(htmlToText(loaderData.content_html || "")),
    ];
    const description = candidates.find((c) => c.length >= 50) ?? candidates.find(Boolean) ?? "";
    const ogImage = loaderData.og_image_url || loaderData.featured_image_url;
    const videos = postVideoObjects(loaderData.slug);
    const dates = postDates({
      slug: loaderData.slug,
      publishedAt: loaderData.published_at,
      updatedAt: loaderData.updated_at,
      wpParity: loaderData.wp_parity === true,
    });
    return {
      meta: [
        // Drop the brand suffix when it would push the title past 60 characters.
        // A restored WordPress title is used as it is (see src/lib/wp-parity.server.ts).
        { title: loaderData.wp_parity ? title : brandedTitle(title, "RESTORE Hair") },
        { name: "description", content: description },
        { property: "og:title", content: title },
        { property: "og:description", content: description },
        { property: "og:type", content: "article" },
        ...(ogImage ? [
          { property: "og:image", content: absoluteUrl(ogImage) },
          { name: "twitter:image", content: absoluteUrl(ogImage) },
        ] : []),
        { name: "twitter:card", content: "summary_large_image" },
        { name: "twitter:title", content: title },
        { name: "twitter:description", content: description },
      ],
      links: [{ rel: "canonical", href: absoluteUrl(`/${loaderData.slug}/`) }],
      scripts: [
        {
          type: "application/ld+json",
          children: safeJsonLd({
            "@context": "https://schema.org",
            "@type": "BlogPosting",
            headline: loaderData.title,
            image: ogImage ? [absoluteUrl(ogImage)] : undefined,
            description,
            // A Person only when blog_authors names a real person. Otherwise
            // the post is credited to the organization. No reviewedBy: posts
            // are by RESTORE Hair and claim no medical review (Brian, 31 Oct).
            author: blogAuthorNode(loaderData.author),
            publisher: { "@id": ORGANIZATION_ID },
            // WordPress dates where the post came from WordPress; never an
            // import date (postDates in src/lib/blog-eeat.ts).
            datePublished: dates.published ?? undefined,
            dateModified: dates.modified ?? undefined,
            citation: postCitations(loaderData.slug, loaderData.content_html || ""),
            mainEntityOfPage: {
              "@type": "WebPage",
              "@id": `https://restorehair.com/${loaderData.slug}/`,
            },
          }),
        },
        ...videos.map((v) => ({ type: "application/ld+json", children: safeJsonLd(v) })),
        {
          type: "application/ld+json",
          children: safeJsonLd(
            breadcrumbSchema([
              { name: "Blog", path: "/hair-loss-blog/" },
              { name: loaderData.title, path: `/${loaderData.slug}/` },
            ]),
          ),
        },
      ],
    };
  },
  component: BlogPost,
  errorComponent: ({ error, reset }) => (
    <div className="mx-auto max-w-4xl px-4 py-20 text-center">
      <h1 className="text-2xl font-semibold text-foreground">Couldn't load this post</h1>
      <p className="mt-2 text-sm text-muted-foreground">Something went wrong. Please try again.</p>
      <button onClick={reset} className="mt-4 rounded-md bg-primary px-4 py-2 text-sm text-primary-foreground">Retry</button>
    </div>
  ),
  notFoundComponent: () => <NotFoundPage />,
});

function BlogPost() {
  const { slug } = Route.useParams();
  (globalThis as { __rhMark?: (name: string) => void }).__rhMark?.("react.post");
  const { data: post } = useSuspenseQuery(postQueryOptions(slug));
  (globalThis as { __rhMark?: (name: string) => void }).__rhMark?.("react.post.data");
  if (!post) return null;
  // H2 ids (kept from WordPress, or added) and the contents list built from them.
  const toc = withTableOfContents(
    normalizeBlogTypography(
      // Internal links were already rewritten in getBlogPost.
      demoteHeadings(localizeExternalImages(sanitizeHtml(post.content_html))),
    ),
  );

  return (
    <>
      <Header />
      <article className="mx-auto max-w-3xl px-4 py-12 lg:py-16">
        <nav className="mb-6 text-sm">
          <Link to="/hair-loss-blog/" className="text-muted-foreground hover:text-primary">← All posts</Link>
        </nav>
        {post.featured_image_url ? (
          <header className="mb-8">
            <div className="relative overflow-hidden rounded-lg bg-foreground">
              <img
                src={post.featured_image_url}
                alt={`${post.title} featured image`}
                className="w-full"
              />
              <h1 className="absolute inset-y-0 left-0 flex w-[46%] items-center px-5 text-xl font-bold leading-tight text-background sm:px-8 sm:text-3xl lg:text-4xl">
                {post.title}
              </h1>
            </div>
          </header>
        ) : (
          <header className="mb-8">
            <h1 className="text-3xl font-bold leading-tight text-foreground lg:text-4xl">{post.title}</h1>
          </header>
        )}
        <PostByline
          author={post.author}
          slug={post.slug}
          publishedAt={post.published_at}
          updatedAt={post.updated_at}
          wpParity={post.wp_parity === true}
        />
        <TableOfContents items={toc.items} />
        <PostBody html={toc.html} videos={POST_VIDEOS[post.slug] ?? []} />
        <PostSources sources={POST_SOURCES[post.slug]} />
        {/* A second quote block for readers who finish the post. Same approved
            wording as the one after the first paragraph. */}
        <InlineQuoteCta />
        <RelatedArticles posts={post.related} />
      </article>
      <Footer />
    </>
  );
}
// The post body with a short quote block after the first paragraph. Most
// readers land here from search or an AI assistant and see no quote action
// until the end of the post. When the HTML has no clear first paragraph, the
// body renders as before, with no block.
function PostBody({ html, videos }: { html: string; videos: PostVideoEntry[] }) {
  // Each video sits where it was on WordPress (placeVideos). The quote block
  // goes after the first paragraph of the first piece of text.
  const segments = placeVideos(html, videos);
  let ctaPlaced = false;
  return (
    <>
      {segments.map((segment, i) => {
        if ("video" in segment)
          return <PostVideo key={`v-${segment.video.key}`} video={segment.video} />;
        const parts = ctaPlaced ? null : splitAfterFirstParagraph(segment.html);
        if (!parts) {
          return (
            <div
              key={i}
              className="blog-post-content"
              dangerouslySetInnerHTML={{ __html: segment.html }}
            />
          );
        }
        ctaPlaced = true;
        return (
          <Fragment key={i}>
            <div className="blog-post-content" dangerouslySetInnerHTML={{ __html: parts[0] }} />
            <InlineQuoteCta />
            <div className="blog-post-content" dangerouslySetInnerHTML={{ __html: parts[1] }} />
          </Fragment>
        );
      })}
    </>
  );
}

// Links to other posts, rendered on the server as plain anchors. See
// src/lib/related-posts.ts for how the posts are chosen.
function RelatedArticles({ posts }: { posts: { slug: string; title: string }[] }) {
  if (posts.length === 0) return null;
  return (
    <nav aria-labelledby="related-articles" className="mt-12 border-t border-border pt-8">
      <h2 id="related-articles" className="text-xl font-bold text-foreground">Related articles</h2>
      <ul className="mt-4 space-y-3">
        {posts.map((p) => (
          <li key={p.slug}>
            <a href={`/${p.slug}/`} className="text-primary underline underline-offset-2 hover:opacity-80">
              {p.title}
            </a>
          </li>
        ))}
      </ul>
    </nav>
  );
}

// The visible author and dates. The BlogPosting JSON-LD names the same
// author, and search engines expect the credited author to be visible on the
// page. A date reads as its calendar day in its
// own offset (a WordPress date in UTC-5), the same on the server and in the
// browser.
function formatDate(value: string): string {
  return new Date(`${localCalendarDate(value)}T00:00:00Z`).toLocaleDateString("en-US", {
    year: "numeric",
    month: "long",
    day: "numeric",
    timeZone: "UTC",
  });
}

function PostByline({
  author,
  slug,
  publishedAt: storedPublishedAt,
  updatedAt,
  wpParity,
}: {
  author: Parameters<typeof isPersonAuthor>[0];
  slug: string;
  publishedAt: string | null | undefined;
  updatedAt: string | null | undefined;
  wpParity: boolean;
}) {
  const { published: publishedAt, updated }: PostDates = postDates({
    slug,
    publishedAt: storedPublishedAt,
    updatedAt,
    wpParity,
  });
  return (
    <div className="mb-8 space-y-1 text-sm text-muted-foreground">
      <p>
        By{" "}
        {isPersonAuthor(author) ? (
          <a
            href={`/author/${author.slug}/`}
            className="font-semibold text-foreground hover:text-primary"
          >
            {author.name.trim()}
          </a>
        ) : (
          <span className="font-semibold text-foreground">RESTORE Hair</span>
        )}
        {publishedAt ? (
          <>
            {" · Published "}
            <time dateTime={publishedAt}>{formatDate(publishedAt)}</time>
          </>
        ) : null}
        {updated ? (
          <>
            {" · Updated "}
            <time dateTime={updated}>{formatDate(updated)}</time>
          </>
        ) : null}
      </p>
    </div>
  );
}

// "In this article": links to the post's H2s. WordPress showed one on many
// posts, and its #t-… links were the URLs Google listed as "jump to"
// results. Server-rendered plain anchors.
function TableOfContents({ items }: { items: TocItem[] }) {
  if (items.length === 0) return null;
  return (
    <nav aria-labelledby="in-this-article" className="mb-8 border border-border px-5 py-4">
      <h2 id="in-this-article" className="text-base font-bold text-foreground">
        In this article
      </h2>
      <ol className="mt-3 list-decimal space-y-1 pl-5 text-sm">
        {items.map((item) => (
          <li key={item.id}>
            <a
              href={`#${item.id}`}
              className="text-primary underline underline-offset-2 hover:opacity-80"
            >
              {item.text}
            </a>
          </li>
        ))}
      </ol>
    </nav>
  );
}

// The sources listed for the post in POST_SOURCES (src/lib/blog-eeat.ts).
function PostSources({ sources }: { sources: PostSource[] | undefined }) {
  if (!sources?.length) return null;
  return (
    <section aria-labelledby="post-sources" className="mt-10 border-t border-border pt-6">
      <h2 id="post-sources" className="text-xl font-bold text-foreground">
        Sources
      </h2>
      <ol className="mt-4 list-decimal space-y-2 pl-5 text-sm text-muted-foreground">
        {sources.map((s) => (
          <li key={s.url}>
            <a
              href={s.url}
              target="_blank"
              rel="noopener"
              className="text-primary underline underline-offset-2 hover:opacity-80"
            >
              {s.title}
            </a>
            {s.publisher ? `, ${s.publisher}` : null}
          </li>
        ))}
      </ol>
    </section>
  );
}

// One of the post's videos from POST_VIDEOS, in its WordPress place. A file
// plays in a <video>; ?t=<seconds>&v=<key> (a key moment's Clip URL) starts
// it at that time. A YouTube video shows its thumbnail (served from this site)
// and loads the youtube-nocookie player only when the visitor clicks play.
function PostVideo({ video }: { video: PostVideoEntry }) {
  return (
    <figure id={`video-${video.key}`} className="my-10">
      {video.kind === "file" ? <FileVideo video={video} /> : <YouTubeFacade video={video} />}
      {video.caption ? (
        <figcaption className="mt-2 text-sm text-muted-foreground">{video.caption}</figcaption>
      ) : null}
    </figure>
  );
}

function FileVideo({ video }: { video: Extract<PostVideoEntry, { kind: "file" }> }) {
  const ref = useRef<HTMLVideoElement>(null);
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const t = Number(params.get("t"));
    const el = ref.current;
    if (!el || params.get("v") !== video.key || !Number.isFinite(t) || t <= 0) return;
    el.currentTime = t;
    el.scrollIntoView({ block: "center" });
  }, [video.key]);
  return (
    <video
      ref={ref}
      controls
      playsInline
      preload="none"
      poster={video.thumbnailUrl}
      aria-label={video.name}
      src={video.contentUrl}
      width={video.width}
      height={video.height}
      style={{ aspectRatio: `${video.width} / ${video.height}` }}
      className="mx-auto block h-auto max-h-[80vh] w-auto max-w-full rounded-lg bg-foreground"
    />
  );
}

function YouTubeFacade({ video }: { video: Extract<PostVideoEntry, { kind: "youtube" }> }) {
  const [playing, setPlaying] = useState(false);
  return (
    <div className="relative aspect-video w-full overflow-hidden rounded-lg bg-foreground">
      {playing ? (
        <iframe
          src={`${youtubeEmbedUrl(video.youtubeId)}?autoplay=1&rel=0`}
          title={video.name}
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
          referrerPolicy="strict-origin-when-cross-origin"
          allowFullScreen
          className="absolute inset-0 h-full w-full border-0"
        />
      ) : (
        <button
          type="button"
          onClick={() => setPlaying(true)}
          aria-label={`Play video: ${video.name}`}
          className="group absolute inset-0 h-full w-full"
        >
          <img
            src={video.thumbnailUrl}
            alt=""
            loading="lazy"
            className="h-full w-full object-cover"
          />
          <PlayButton className="absolute left-1/2 top-1/2 h-16 w-16 -translate-x-1/2 -translate-y-1/2 text-background transition group-hover:scale-110" />
        </button>
      )}
    </div>
  );
}
